import { logger } from './devUtils';

/**
 * 播放器错误类型
 */
export interface PlayerError {
  type: 'init' | 'play' | 'load' | 'network' | 'decode' | 'unknown';
  code: string | number;
  message: string;
  timestamp: number;
  context: {
    cid?: string;
    vid?: string;
    url?: string;
    [key: string]: any;
  };
}

/**
 * 播放器错误监控类
 * 负责捕获、记录和上报播放器错误
 */
class PlayerErrorMonitor {
  private static instance: PlayerErrorMonitor;

  /**
   * 获取单例实例
   */
  static getInstance(): PlayerErrorMonitor {
    if (!PlayerErrorMonitor.instance) {
      PlayerErrorMonitor.instance = new PlayerErrorMonitor();
    }
    return PlayerErrorMonitor.instance;
  }

  private errors: PlayerError[] = [];
  private readonly MAX_ERRORS = 50; // 最多保存50条错误

  private constructor() {
    // 私有构造函数，防止外部实例化
  }

  /**
   * 记录错误
   * @param error 错误信息（不包含时间戳，会自动添加）
   */
  logError(error: Omit<PlayerError, 'timestamp'>) {
    const errorWithTime: PlayerError = {
      ...error,
      timestamp: Date.now(),
    };

    // 保存到本地数组
    this.errors.push(errorWithTime);
    if (this.errors.length > this.MAX_ERRORS) {
      this.errors.shift(); // 移除最老的错误
    }

    // 输出到控制台
    logger.error('[player] 错误', {
      type: errorWithTime.type,
      code: errorWithTime.code,
      message: errorWithTime.message,
      time: new Date(errorWithTime.timestamp).toISOString(),
      context: errorWithTime.context,
    });

    // 上报到服务器
    this.reportError(errorWithTime);

    // 可选：保存到 localStorage 用于持久化
    this.saveToLocalStorage();
  }

  /**
   * 从 localStorage 加载错误
   */
  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem('player_errors');
      if (saved) {
        const errors = JSON.parse(saved) as PlayerError[];
        logger.log('[player] 从 localStorage 加载了', errors.length, '条历史错误');
        return errors;
      }
    } catch (err) {
      logger.warn('[player] 从 localStorage 加载失败:', err);
    }
    return [];
  }

  /**
   * 获取所有错误（用于排查）
   */
  getErrors(): PlayerError[] {
    return [...this.errors];
  }

  /**
   * 获取特定类型的错误
   */
  getErrorsByType(type: PlayerError['type']): PlayerError[] {
    return this.errors.filter((error) => error.type === type);
  }

  /**
   * 获取最近的错误
   */
  getRecentErrors(count = 10): PlayerError[] {
    return this.errors.slice(-count);
  }

  /**
   * 清空错误记录
   */
  clearErrors() {
    this.errors = [];
    try {
      localStorage.removeItem('player_errors');
    } catch (err) {
      logger.warn('[player] 清除 localStorage 失败:', err);
    }
    logger.log('[player] 错误记录已清空');
  }

  /**
   * 导出错误日志（用于问题排查）
   */
  exportErrors(): string {
    return JSON.stringify(this.errors, null, 2);
  }

  /**
   * 导出为可读的文本格式
   */
  exportAsText(): string {
    return this.errors
      .map((error) => {
        const time = new Date(error.timestamp).toISOString();
        const contextStr = JSON.stringify(error.context);
        return `[${time}] [${error.type}] ${error.code}: ${error.message}\nContext: ${contextStr}\n`;
      })
      .join('\n---\n\n');
  }

  /**
   * 获取错误统计信息
   */
  getStatistics() {
    const stats: Record<string, number> = {};

    this.errors.forEach((error) => {
      const key = error.type;
      stats[key] = (stats[key] || 0) + 1;
    });

    return {
      total: this.errors.length,
      byType: stats,
      oldest: this.errors[0]?.timestamp,
      newest: this.errors[this.errors.length - 1]?.timestamp,
    };
  }

  /**
   * 上报错误到服务器
   */
  private reportError(error: PlayerError) {
    try {
      // 构建上报数据
      const reportData = {
        ...error,
        userAgent: navigator.userAgent,
        url: window.location.href,
        screen: {
          width: window.screen.width,
          height: window.screen.height,
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      };

      // 发送上报请求
      logger.log('[player] 上报数据:', reportData);
      window.AegisV2?.reportEvent({
        name: 'playerError',
        ext3: JSON.stringify(reportData),
      });
    } catch (err) {
      logger.warn('[player] 上报异常:', err);
    }
  }

  /**
   * 保存错误到 localStorage
   */
  private saveToLocalStorage() {
    try {
      const recentErrors = this.errors.slice(-10); // 只保存最近10条
      localStorage.setItem('player_errors', JSON.stringify(recentErrors));
    } catch (err) {
      logger.warn('[player] 保存到 localStorage 失败:', err);
    }
  }
}

// 导出单例实例
export const playerErrorMonitor = PlayerErrorMonitor.getInstance();
